'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import { projectsService } from '@/features/project/api/projects';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { Button } from '@/shared/ui/shadcn/button';
import { Input } from '@/shared/ui/shadcn/input';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { Label } from '@/shared/ui/shadcn/label';
import { Typography } from '@/shared/ui/shadcn/typography';
import { LoadingSpinner } from '@/shared/ui';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/shadcn/popover';
import { HelpCircle } from 'lucide-react';
import {
  FacadeAreaField,
  parseFacadeArea,
} from '@/features/project/ui/FacadeAreaField';
import type { PanelType } from '@/shared/hooks';
import type { Project } from '@/types/project';

interface ProjectSettingsPanelProps {
  project: Project;
  panel: PanelType;
  isArchived: boolean;
}

/**
 * Panel component for displaying and editing project settings.
 * Goals panel now includes Construction Region field.
 */
export function ProjectSettingsPanel({
  project,
  panel,
  isArchived,
}: ProjectSettingsPanelProps) {
  const t = useTranslations('project');
  const queryClient = useQueryClient();

  const [goalsValue, setGoalsValue] = useState('');
  const [regionValue, setRegionValue] = useState('');
  const [areaValue, setAreaValue] = useState('');
  const [saving, setSaving] = useState(false);

  const projectId = project.id;

  const serverArea =
    project.attributes.facade_area != null
      ? String(project.attributes.facade_area)
      : '';
  // Only an actually-edited area rides along in the PATCH — sending the field
  // (even unchanged) marks it user-owned and clears the "estimated" flag.
  const areaChanged = areaValue.trim() !== serverArea;

  // Initialize form values when panel opens or project changes
  useEffect(() => {
    if (panel === 'goals') {
      setGoalsValue(project.attributes.goals || '');
      setRegionValue(project.attributes.construction_region || '');
      setAreaValue(
        project.attributes.facade_area != null
          ? String(project.attributes.facade_area)
          : ''
      );
    }
  }, [panel, project]);

  const handleSaveGoals = async () => {
    if (isArchived || saving) return;
    setSaving(true);
    try {
      const pendingArea = areaChanged ? parseFacadeArea(areaValue) : undefined;
      await projectsService.update(projectId, {
        goals: goalsValue.trim() || null,
        construction_region: regionValue.trim() || null,
        ...(pendingArea !== undefined ? { facade_area: pendingArea } : {}),
      });
      await queryClient.invalidateQueries({
        queryKey: cacheKeys.projects.detail(projectId),
      });
      // Refetch after 1s to get AI-generated title
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: cacheKeys.projects.detail(projectId),
        });
      }, 1000);
    } catch (error) {
      console.error('Failed to save goals:', error);
    } finally {
      setSaving(false);
    }
  };

  if (panel === 'goals') {
    const hasGoalsChanges = goalsValue !== (project.attributes.goals || '');
    const hasRegionChanges =
      regionValue !== (project.attributes.construction_region || '');
    const hasChanges = hasGoalsChanges || hasRegionChanges || areaChanged;

    return (
      <div className="p-6">
        <div className="max-w-2xl">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Typography variant="h3">{t('projectGoals')}</Typography>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <HelpCircle className="h-4 w-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80" align="start">
                  <div className="space-y-2">
                    <h4 className="text-list-item-value">
                      {t('projectGoalsHelp')}
                    </h4>
                    <p className="text-form-help text-muted-foreground">
                      {t('projectGoalsHelpText')}
                    </p>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <Typography variant="muted" className="text-form-help">
              {t('projectGoalsHelpText')}
            </Typography>
          </div>

          <div className="space-y-6">
            {/* Goals textarea */}
            <div>
              <Label htmlFor="project-goals" className="sr-only">
                {t('projectGoalsLabel')}
              </Label>
              <Textarea
                id="project-goals"
                value={goalsValue}
                onChange={(e) => setGoalsValue(e.target.value)}
                placeholder={t('projectGoalsPlaceholder')}
                className="min-h-[200px]"
                maxLength={10000}
                disabled={isArchived || saving}
              />
            </div>

            {/* Construction Region field */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="construction-region">
                  {t('constructionRegion')}
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <HelpCircle className="h-4 w-4" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80" align="start">
                    <p className="text-form-help text-muted-foreground">
                      {t('constructionRegionHelpText')}
                    </p>
                  </PopoverContent>
                </Popover>
              </div>
              <Input
                id="construction-region"
                type="text"
                value={regionValue}
                onChange={(e) => setRegionValue(e.target.value)}
                placeholder={t('constructionRegionPlaceholder')}
                maxLength={255}
                disabled={isArchived || saving}
              />
            </div>

            {/* Facade area — optional; estimates use it as the authoritative
                building area instead of re-guessing from photos. */}
            <FacadeAreaField
              id="panel-facade-area"
              value={areaValue}
              unit={project.attributes.facade_area_unit ?? 'm2'}
              isEstimated={
                !!project.attributes.facade_area_is_estimated && !areaChanged
              }
              disabled={isArchived || saving}
              onChange={setAreaValue}
              onBlur={() => undefined}
            />

            {hasChanges && (
              <Button
                onClick={handleSaveGoals}
                disabled={isArchived || saving}
                className="w-full sm:w-auto"
              >
                {saving ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    {t('saving')}
                  </>
                ) : (
                  t('save')
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
}
